import { useState, useEffect, useRef } from 'react';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import {
  useAdminPackages,
  useCreatePackage,
  useUpdatePackage,
  useDeletePackage,
  useClonePackage,
  usePublishPackageToShop,
  usePackageFeatureCoverage,
  useGatingMode,
  useFeatureRegistry,
} from '@/hooks/useAdmin';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Toggle } from '@/components/ui/Toggle';
import { Checkbox } from '@/components/ui/Checkbox';
import { Modal } from '@/components/ui/Modal';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { ConfirmDialog, Card, EmptyState, Pagination } from '@/components/ui';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ActionButton } from '@/components/admin/ActionButton';
import { PackageEntitlementsSection, PORTFOLIO_ONLY_GROUPS, type PackageDraft } from '@/components/admin/PackageEntitlementsSection';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { toast } from '@/utils/toast';
import { adminService } from '@/services/admin.service';
import { useQueryClient } from '@tanstack/react-query';
import { ICONS } from '@/constants/icons';
import { formatApiError } from '@/services/api';
import { parseIntegerInput } from '@/utils/parseIntegerInput';
import { useTimezone } from '@/contexts/TimezoneContext';
import { PackageWcSyncStatus, AdminPackageFeatureRow, PackageCoverageGap } from '@/types/admin';
import { getWcProductEditUrl } from '@/constants/shop';
import { GATED_PLATFORMS, type GatedPlatform } from '@/types/package';
import { GATED_PLATFORM_LABELS } from '@/components/gating/gatingCopy';

/** Return a copy of `obj` without the listed keys. */
function omitKeys<T extends object, K extends keyof T>(obj: T, keys: readonly K[]): Omit<T, K> {
  const copy: T = { ...obj };
  for (const k of keys) delete copy[k];
  return copy;
}

/**
 * Admin Packages Page - Modernized Card Layout
 *
 * Data Structure Note:
 * Backend returns packages with fields at TOP LEVEL (not nested):
 * - maxChannels, maxUsers, maxCalendars, maxVariations, maxBrandProfiles, maxPostsPerMonth, maxAITokens
 * - canvaEnabled, brandCheckEnabled, whitelabelEnabled
 * - priceMonthly, priceYearly, source, isAgency, maxStorage, maxWorkspaces
 */

interface BackendPackage {
  id: number;
  name: string;
  description: string | null;
  source: 'DIRECT' | 'APPSUMO' | 'STRIPE' | 'TRIAL' | 'MANUAL';
  maxChannels: number | null;
  maxUsers: number | null;
  maxAITokens: number;
  maxStorage: number;
  maxPostsPerMonth: number | null;
  priceMonthly: number;
  priceYearly: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  brandCheckEnabled: boolean;
  canvaEnabled: boolean;
  isAgency: boolean;
  isLifetime: boolean;
  maxCalendars: number | null;
  maxVariations: number | null;
  /** Brand World profiles per brand (null = unlimited) */
  maxBrandProfiles: number | null;
  maxWorkspaces: number | null;
  whitelabelEnabled: boolean;
  analyticsEnabled: boolean;
  gdriveEnabled?: boolean;
  /** false = every platform may be connected */
  platformsRestricted?: boolean;
  /** Platforms a restricted package allows (the 9 social ones) */
  allowedPlatforms?: string[];
  slug: string | null;
  wcProductId: number | null;
  /** Set when the shop product is a variable subscription (Monthly + Yearly). */
  wcVariationMonthlyId: number | null;
  wcVariationYearlyId: number | null;
  wcSyncStatus: PackageWcSyncStatus;
  wcSyncedAt: string | null;
  wcSyncError: string | null;
  /**
   * Elastic PackageFeature rows (only `isActive: true` ones — see
   * findAllWithFeatures/findByIdWithFeatures on the backend). Distinct from
   * the dedicated boolean columns above (canvaEnabled, brandCheckEnabled,
   * etc.) — used for features like `wordpress` that don't have their own
   * Package column. See AdminPackageFeatureRow.
   */
  packageFeatures: AdminPackageFeatureRow[];
}

/**
 * WordPress Blog publishing feature name in the elastic PackageFeature
 * registry (backend `FEATURE_NAMES.WORDPRESS`). Kept as a local constant
 * rather than importing the backend constants file (frontend doesn't share
 * that module) — mirrors how 'canva'/'brand_check' are hardcoded as flat
 * boolean fields elsewhere on this page.
 */
const WORDPRESS_FEATURE_NAME = 'wordpress';

/** True if the package's elastic feature rows have `wordpress` enabled. */
const isWordPressEnabled = (pkg: BackendPackage): boolean =>
  pkg.packageFeatures?.some(
    (f) => f.featureName === WORDPRESS_FEATURE_NAME && f.isActive && f.featureValue === 'true'
  ) ?? false;

interface AdminPackageFilters {
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'name';
  sortOrder?: 'asc' | 'desc';
  search?: string;
  isActive?: boolean;
}

export const AdminPackages = () => {
  const [filters, setFilters] = useState<AdminPackageFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<BackendPackage | null>(null);
  const [justCreated, setJustCreated] = useState(false);
  const [cloneDialog, setCloneDialog] = useState<{ open: boolean; pkg: BackendPackage | null }>({ open: false, pkg: null });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; pkg: BackendPackage | null }>({ open: false, pkg: null });

  const { data, isLoading, error } = useAdminPackages({ ...filters, search: search || undefined });
  const queryClient = useQueryClient();
  const createPackage = useCreatePackage();
  const updatePackage = useUpdatePackage();
  const deletePackage = useDeletePackage();
  const clonePackage = useClonePackage();
  const publishToShop = usePublishPackageToShop();
  const { data: coverage } = usePackageFeatureCoverage();
  const { data: gatingMode } = useGatingMode();

  const packages = (data as any)?.data as BackendPackage[] | undefined;

  // Search is live (query key includes `search` directly) — reset to page 1
  // whenever the query changes, same as the old submit-triggered handleSearch did.
  useEffect(() => {
    setFilters((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [search]);

  const handleCreate = () => {
    setEditingPackage(null);
    setJustCreated(false);
    setShowModal(true);
  };

  const handleEdit = (pkg: BackendPackage) => {
    setEditingPackage(pkg);
    setShowModal(true);
  };

  const handleCloneConfirm = async () => {
    if (!cloneDialog.pkg) return;

    try {
      const result = await clonePackage.mutateAsync(cloneDialog.pkg.id);
      toast.success(`Package cloned: ${(result as any).name}`);
      setCloneDialog({ open: false, pkg: null });
    } catch (err: unknown) {
      toast.error(formatApiError(err).message || 'Failed to clone package');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.pkg) return;

    try {
      await deletePackage.mutateAsync(deleteDialog.pkg.id);
      toast.success(`Package ${deleteDialog.pkg.name} deleted`);
      setDeleteDialog({ open: false, pkg: null });
    } catch (err: unknown) {
      toast.error(formatApiError(err).message || 'Failed to delete package');
    }
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  const handlePublishToShop = async (pkg: BackendPackage) => {
    try {
      const result = await publishToShop.mutateAsync(pkg.id);
      if (result.wcSyncStatus === 'FAILED') {
        toast.error(result.wcSyncError || 'Shop sync failed again — check the shop connection');
      } else if (result.wcSyncStatus === 'DRAFT_CREATED') {
        toast.success('Draft product created in shop — Kristin can polish + publish');
      } else if (result.wcSyncStatus === 'PUBLISHED') {
        toast.success('Package is live in shop');
      } else {
        toast.info('Shop sync retried');
      }
    } catch (err: unknown) {
      toast.error(formatApiError(err).message || 'Failed to publish to shop');
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        Error loading packages: {formatApiError(error).message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Packages"
        subtitle="Manage subscription packages and pricing tiers"
        action={<Button onClick={handleCreate}>Create Package</Button>}
      />

      <GatingStatus mode={gatingMode ?? null} gaps={coverage?.gaps ?? []} />

      {/* Search & Filters */}
      <Card padding="sm">
        <form onSubmit={(e) => e.preventDefault()} className="flex gap-4 flex-wrap">
          <Input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[200px]"
          />
          <Select
            value={filters.isActive?.toString() || ''}
            onValueChange={(value) => setFilters({ ...filters, isActive: value === '' ? undefined : value === 'true', page: 1 })}
            options={[
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' }
            ]}
            placeholder="All Status"
            clearable
            clearLabel="All Status"
            className="min-w-[150px]"
          />
        </form>
      </Card>

      {/* Packages Grid */}
      {isLoading ? (
        <Card className="p-12 flex justify-center">
          <LoadingSpinner />
        </Card>
      ) : packages && packages.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <PackageCard
              key={pkg.id}
              package={pkg}
              onEdit={handleEdit}
              onClone={(pkg) => setCloneDialog({ open: true, pkg })}
              onDelete={(pkg) => setDeleteDialog({ open: true, pkg })}
              onPublishToShop={handlePublishToShop}
              isCloning={clonePackage.isPending}
              isDeleting={deletePackage.isPending}
              isPublishingToShop={publishToShop.isPending && publishToShop.variables === pkg.id}
            />
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState title="No packages found" />
        </Card>
      )}

      {/* Pagination */}
      {data && (data as any).meta?.totalPages > 1 && (
        <Card padding="sm" className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {(((data as any).meta.page - 1) * (data as any).meta.limit) + 1} to {Math.min((data as any).meta.page * (data as any).meta.limit, (data as any).meta.total)} of {(data as any).meta.total}
          </div>
          <Pagination
            page={(data as any).meta.page}
            totalPages={(data as any).meta.totalPages}
            onPageChange={handlePageChange}
          />
        </Card>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <PackageModal
          key={editingPackage?.id ?? 'new'}
          package={editingPackage}
          justCreated={justCreated}
          onClose={() => {
            setShowModal(false);
            setJustCreated(false);
          }}
          onSave={async (formData) => {
            let createdFallback: BackendPackage | null = null;
            try {
              // Create: `entitlements` (row switches, incl. wordpress) rides in the create payload and the
              // monthly limits are PUT after the id is known. In edit mode the Feature access section owns
              // canva, brand check, analytics, atelier mode and wordpress (saved instantly), so their stale
              // form values are never sent.
              const { quotas: draftQuotaList, ...allData } = formData as typeof formData & {
                quotas?: { quotaKey: string; monthlyLimit: number }[];
              };
              const columnsWithoutToggles = omitKeys(allData, [
                'canvaEnabled',
                'brandCheckEnabled',
                'analyticsEnabled',
                'gdriveEnabled',
                'whitelabelEnabled',
              ]);
              const packageData = editingPackage ? columnsWithoutToggles : allData;
              let createdId: number | null = null;

              if (editingPackage) {
                const result = await updatePackage.mutateAsync({ id: editingPackage.id, data: packageData });
                toast.success('Package updated');

                const wcSync = result.wcSync;
                if (wcSync?.error) {
                  toast.warning(`Package updated, but shop sync failed: ${wcSync.error}`);
                } else if (wcSync) {
                  if (wcSync.synced.length > 0) {
                    const parts = [`Synced to shop: ${wcSync.synced.join(', ')}`];
                    if (wcSync.preserved.length > 0) {
                      parts.push(`Preserved (edited in shop): ${wcSync.preserved.join(', ')}`);
                    }
                    toast.info(parts.join(' · '));
                  } else if (wcSync.preserved.length > 0) {
                    toast.info(
                      `Nothing changed in the shop — your edits there were preserved: ${wcSync.preserved.join(', ')}`
                    );
                  }
                }
              } else {
                const result = await createPackage.mutateAsync(packageData);
                toast.success('Package created');
                createdId = result.id;
                createdFallback = result as unknown as BackendPackage;

                if (result.wcSyncStatus === 'FAILED') {
                  toast.warning('Package created, but shop draft failed — retry from the list');
                } else if (result.wcSyncStatus === 'DRAFT_CREATED') {
                  toast.info('Draft product created in shop — Kristin can polish + publish');
                }
              }

              // Monthly limits (create only): sequential, a failure never blocks the rest or duplicates the package
              const failedQuotas: string[] = [];
              if (createdId !== null && draftQuotaList?.length) {
                for (const q of draftQuotaList) {
                  try {
                    await adminService.setPackageQuota(createdId, q.quotaKey, q.monthlyLimit);
                  } catch {
                    failedQuotas.push(q.quotaKey);
                  }
                }
                if (failedQuotas.length > 0) {
                  toast.error(
                    `Package created, but these monthly limits did not save: ${failedQuotas.join(', ')}. Set them again below.`
                  );
                }
              }

              if (createdId !== null) {
                // Stay in the modal and switch to edit mode for the new package
                // so Feature access / Monthly limits / Platforms are available.
                await queryClient.invalidateQueries({ queryKey: ['admin', 'packages'] });
                const fresh = await adminService.getPackageById(createdId);
                setEditingPackage(fresh as unknown as BackendPackage);
                setJustCreated(true);
              } else {
                setShowModal(false);
              }
            } catch (err: unknown) {
              toast.error(formatApiError(err).message || 'Failed to save package');
              // The package exists already (a later step failed): switch to edit mode so a
              // retry updates it instead of creating a duplicate. A failed create stays in create mode.
              if (createdFallback) {
                setEditingPackage(createdFallback);
                setJustCreated(true);
              }
            }
          }}
          isSaving={createPackage.isPending || updatePackage.isPending}
          onSaveBrandProfiles={async (id, value) => {
            await updatePackage.mutateAsync({ id, data: { maxBrandProfiles: value } });
          }}
        />
      )}

      {/* Clone Confirmation Dialog */}
      <ConfirmDialog
        open={cloneDialog.open}
        onOpenChange={(open) => !open && setCloneDialog({ open: false, pkg: null })}
        title="Clone Package"
        description={`Clone "${cloneDialog.pkg?.name}"? A copy will be created with "(Copy)" appended to the name.`}
        confirmText="Clone"
        variant="default"
        onConfirm={handleCloneConfirm}
        loading={clonePackage.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, pkg: null })}
        title="Delete Package"
        description={`Are you sure you want to delete "${deleteDialog.pkg?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        loading={deletePackage.isPending}
      />
    </div>
  );
};

// Gating mode note + coverage banner
const GATING_MODE_COPY: Record<'off' | 'shadow' | 'enforce', string> = {
  off: 'Gating mode: off. Feature switches are stored but not enforced for new features.',
  shadow: 'Gating mode: shadow. Denials are logged only, nobody is blocked yet.',
  enforce: 'Gating mode: enforce. Switched-off features are locked for teams on that package.',
};

const GatingStatus = ({
  mode,
  gaps,
}: {
  mode: 'off' | 'shadow' | 'enforce' | null;
  gaps: PackageCoverageGap[];
}) => (
  <div className="space-y-3">
    {mode && (
      <p className="text-xs text-gray-500" data-testid="gating-mode-note">
        {GATING_MODE_COPY[mode]}
      </p>
    )}
    {gaps.length > 0 && (
      <div
        role="alert"
        data-testid="coverage-banner"
        className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
      >
        <p className="font-medium flex items-center gap-2">
          <i className={`hm hm-${ICONS.WARNING}`} aria-hidden="true" />
          {gaps.length} active {gaps.length === 1 ? 'package has' : 'packages have'} features that are not set
        </p>
        <p className="text-xs mt-1">
          A feature without a row counts as off. Once gating is live these stay locked until you
          switch them on or off explicitly (edit the package, Feature access).
        </p>
        <ul className="mt-2 space-y-1 text-xs">
          {gaps.map((g) => (
            <li key={g.packageId}>
              <span className="font-semibold">{g.name}</span>: {g.missingKeys.length} missing (
              {g.missingKeys.join(', ')})
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

// WC Shop Sync Badge
const WC_SYNC_BADGE_CONFIG: Record<
  Exclude<PackageWcSyncStatus, 'NONE'>,
  { label: string; className: string }
> = {
  PENDING: { label: 'Syncing…', className: 'bg-gray-100 text-gray-500' },
  DRAFT_CREATED: { label: 'Draft in shop', className: 'bg-amber-100 text-amber-700' },
  PUBLISHED: { label: 'Live in shop', className: 'bg-primary-50 text-primary-700' },
  FAILED: { label: 'Shop sync failed', className: 'bg-red-100 text-red-700' },
};

const WcSyncBadge = ({ pkg }: { pkg: BackendPackage }) => {
  if (pkg.wcSyncStatus === 'NONE') {
    // Free packages never get a shop product — don't clutter the card
    if (Number(pkg.priceMonthly) === 0) return null;
    return (
      <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
        Not in shop
      </span>
    );
  }

  const config = WC_SYNC_BADGE_CONFIG[pkg.wcSyncStatus];
  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${config.className}`}>
      {config.label}
    </span>
  );
};

// Package Card Component
interface PackageCardProps {
  package: BackendPackage;
  onEdit: (pkg: BackendPackage) => void;
  onClone: (pkg: BackendPackage) => void;
  onDelete: (pkg: BackendPackage) => void;
  onPublishToShop: (pkg: BackendPackage) => void;
  isCloning: boolean;
  isDeleting: boolean;
  isPublishingToShop: boolean;
}

const PackageCard = ({
  package: pkg,
  onEdit,
  onClone,
  onDelete,
  onPublishToShop,
  isCloning,
  isDeleting,
  isPublishingToShop,
}: PackageCardProps) => {
  const { formatDateOnly } = useTimezone();

  const canRetryOrPublish =
    pkg.wcSyncStatus === 'FAILED' || (pkg.wcSyncStatus === 'NONE' && Number(pkg.priceMonthly) > 0);

  const formatPrice = (monthly: number, yearly: number | null) => {
    if (monthly === 0) return 'Free';
    if (yearly) {
      return (
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900">${monthly}</span>
          <span className="text-sm text-gray-500">/mo</span>
          <span className="text-xs text-gray-400 ml-1">or ${yearly}/yr</span>
        </div>
      );
    }
    return (
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-gray-900">${monthly}</span>
        <span className="text-sm text-gray-500">/mo</span>
      </div>
    );
  };

  const formatLimit = (value: number | null) => (value === null ? '∞' : value.toLocaleString());

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* Actions */}
      <div className="p-4 bg-white flex justify-center gap-2 border-b border-gray-100">
        <ActionButton
          icon={ICONS.EDIT}
          tooltip="Edit Package"
          onClick={() => onEdit(pkg)}
        />
        <ActionButton
          icon="copy"
          tooltip="Clone Package"
          onClick={() => onClone(pkg)}
          disabled={isCloning}
        />
        <ActionButton
          icon={ICONS.DELETE}
          tooltip="Delete Package"
          onClick={() => onDelete(pkg)}
          variant="danger"
          disabled={isDeleting}
        />
      </div>

      {/* Card Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-xl font-semibold text-gray-900">{pkg.name}</h3>
          <div className="flex flex-col items-end gap-1.5">
            <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
              pkg.isActive
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600'
            }`}>
              {pkg.isActive ? 'Active' : 'Inactive'}
            </span>
            <WcSyncBadge pkg={pkg} />
          </div>
        </div>
        {pkg.description && (
          <p className="text-sm text-gray-600 mb-4">{pkg.description}</p>
        )}
        <div className="mb-4">
          {formatPrice(Number(pkg.priceMonthly), pkg.priceYearly ? Number(pkg.priceYearly) : null)}
        </div>
      </div>

      {/* Limits Section */}
      <div className="p-6 bg-gray-50 border-b border-gray-100">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Limits</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">Channels:</span>
            <span className="ml-2 font-medium text-gray-900">{formatLimit(pkg.maxChannels)}</span>
          </div>
          <div>
            <span className="text-gray-500">Users:</span>
            <span className="ml-2 font-medium text-gray-900">{formatLimit(pkg.maxUsers)}</span>
          </div>
          <div>
            <span className="text-gray-500">Calendars:</span>
            <span className="ml-2 font-medium text-gray-900">{formatLimit(pkg.maxCalendars)}</span>
          </div>
          <div>
            <span className="text-gray-500">Posts/mo:</span>
            <span className="ml-2 font-medium text-gray-900">{formatLimit(pkg.maxPostsPerMonth)}</span>
          </div>
          <div>
            <span className="text-gray-500">AI Tokens:</span>
            <span className="ml-2 font-medium text-gray-900">{pkg.maxAITokens.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-gray-500">Storage:</span>
            <span className="ml-2 font-medium text-gray-900">{pkg.maxStorage} MB</span>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="p-6 border-b border-gray-100">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Features</h4>
        <div className="flex flex-wrap gap-2">
          {pkg.canvaEnabled && (
            <span className="px-2.5 py-1 bg-peach text-secondary-700 text-xs font-medium rounded-full">
              Canva
            </span>
          )}
          {pkg.brandCheckEnabled && (
            <span className="px-2.5 py-1 bg-primary-50 text-primary-800 text-xs font-medium rounded-full">
              Brand Check
            </span>
          )}
          {pkg.whitelabelEnabled && (
            <span className="px-2.5 py-1 bg-soft-mint text-secondary-700 text-xs font-medium rounded-full">
              Atelier Mode
            </span>
          )}
          {pkg.isAgency && (
            <span className="px-2.5 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
              Brand Portfolio
            </span>
          )}
          {pkg.analyticsEnabled && (
            <span className="px-2.5 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">
              Analytics
            </span>
          )}
          {isWordPressEnabled(pkg) && (
            <span className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
              WordPress Blog
            </span>
          )}
        </div>
      </div>

      {/* Shop Sync Section */}
      {(pkg.wcSyncStatus !== 'NONE' || pkg.wcProductId || Number(pkg.priceMonthly) > 0) && (
        <div className="p-6 border-b border-gray-100">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Shop Sync</h4>
          <div className="flex items-center flex-wrap gap-3">
            {pkg.wcProductId && (
              <a
                href={getWcProductEditUrl(pkg.wcProductId)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                <i className={`hm hm-${ICONS.EXTERNAL_LINK}`} aria-hidden="true" />
                View in shop
              </a>
            )}
            {pkg.wcVariationMonthlyId && pkg.wcVariationYearlyId && (
              <span className="text-xs text-gray-500">
                Variations: Monthly #{pkg.wcVariationMonthlyId}, Yearly #{pkg.wcVariationYearlyId}
              </span>
            )}
            {canRetryOrPublish && (
              <button
                type="button"
                onClick={() => onPublishToShop(pkg)}
                disabled={isPublishingToShop}
                className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPublishingToShop ? (
                  <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <i className={`hm hm-${ICONS.REFRESH}`} aria-hidden="true" />
                )}
                {pkg.wcSyncStatus === 'FAILED' ? 'Retry' : 'Publish to shop'}
              </button>
            )}
          </div>
          {pkg.wcSyncStatus === 'FAILED' && pkg.wcSyncError && (
            <p className="mt-2 text-xs text-red-600 flex items-start gap-1.5">
              <i className={`hm hm-${ICONS.WARNING} mt-0.5`} aria-hidden="true" />
              <span>{pkg.wcSyncError}</span>
            </p>
          )}
        </div>
      )}

      {/* Metadata */}
      <div className="p-6 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div>
            <span className="font-medium text-gray-700">{pkg.source}</span>
            {pkg.isAgency && pkg.maxWorkspaces && (
              <span className="ml-3">Brand Spaces: {pkg.maxWorkspaces}</span>
            )}
          </div>
          <div>
            {formatDateOnly(pkg.createdAt)}
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal Component
interface PackageModalProps {
  package: BackendPackage | null;
  onClose: () => void;
  justCreated?: boolean;
  onSave: (data: any) => Promise<void>;
  isSaving: boolean;
  /** Edit mode: saves { maxBrandProfiles } alone, immediately (rejects on failure) */
  onSaveBrandProfiles: (packageId: number, value: number | null) => Promise<void>;
}

const PackageModal = ({
  package: pkg,
  onClose,
  onSave,
  isSaving,
  justCreated = false,
  onSaveBrandProfiles,
}: PackageModalProps) => {
  const [formData, setFormData] = useState({
    name: pkg?.name || '',
    description: pkg?.description || '',
    source: pkg?.source || 'DIRECT',
    priceMonthly: (pkg?.priceMonthly ?? 0) as number | null,
    priceYearly: pkg?.priceYearly ?? null,
    maxChannels: pkg?.maxChannels ?? null,
    maxUsers: pkg?.maxUsers ?? null,
    maxCalendars: pkg?.maxCalendars ?? null,
    maxVariations: pkg?.maxVariations ?? null,
    maxBrandProfiles: pkg?.maxBrandProfiles ?? null,
    maxPostsPerMonth: pkg?.maxPostsPerMonth ?? null,
    maxAITokens: (pkg?.maxAITokens ?? 1000) as number | null,
    maxStorage: (pkg?.maxStorage ?? 5000) as number | null,
    maxWorkspaces: pkg?.maxWorkspaces ?? null,
    canvaEnabled: pkg?.canvaEnabled ?? (pkg ? false : true),
    brandCheckEnabled: pkg?.brandCheckEnabled ?? (pkg ? false : true),
    whitelabelEnabled: pkg?.whitelabelEnabled || false,
    isAgency: pkg?.isAgency || false,
    isLifetime: pkg?.isLifetime || false,
    analyticsEnabled: pkg?.analyticsEnabled ?? (pkg ? false : true),
    gdriveEnabled: pkg?.gdriveEnabled ?? (pkg ? false : true),
    isActive: pkg?.isActive ?? true,
    platformsRestricted: pkg?.platformsRestricted ?? false,
    allowedPlatforms: (pkg?.allowedPlatforms ?? []).filter((p): p is GatedPlatform =>
      (GATED_PLATFORMS as readonly string[]).includes(p)
    ),
  });

  const togglePlatform = (platform: GatedPlatform, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      allowedPlatforms: checked
        ? GATED_PLATFORMS.filter((p) => p === platform || prev.allowedPlatforms.includes(p))
        : prev.allowedPlatforms.filter((p) => p !== platform),
    }));
  };

  // Create-mode draft of the Feature access section (row-source switches + monthly limits); nothing is saved until Create
  const [draftToggles, setDraftToggles] = useState<Record<string, boolean>>({});
  const [draftQuotas, setDraftQuotas] = useState<PackageDraft['quotas']>({});
  const [brandProfilesInvalid, setBrandProfilesInvalid] = useState(false);
  const registry = useFeatureRegistry(!pkg);
  const draft: PackageDraft = {
    toggles: draftToggles,
    onToggle: (key, enabled) => setDraftToggles((prev) => ({ ...prev, [key]: enabled })),
    quotas: draftQuotas,
    onQuotaChange: (quota, monthlyLimit) =>
      setDraftQuotas((prev) => ({ ...prev, [quota.quotaKey]: { monthlyLimit, featureKey: quota.featureKey } })),
  };

  // Snapshot the initial values so we can detect edits. The modal is always
  // unmounted on close (see `{showModal && <PackageModal ... />}` above), so
  // no extra open-gating is needed here.
  const initialFormDataRef = useRef(formData);
  const isDirty =
    JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current) ||
    Object.keys(draftToggles).length > 0 ||
    Object.keys(draftQuotas).length > 0;
  useUnsavedGuard(isDirty);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.keys(invalidRaw).length > 0 || brandProfilesInvalid) return;
    if (pkg) {
      onSave(formData);
      return;
    }
    // Create: send only what is visible. Hidden portfolio/release rows are never sent (the backend defaults them ON),
    // and only enforced row-source keys go in `entitlements` (column features stay in the package fields).
    const entries = registry.data?.features ?? [];
    const entitlements: Record<string, boolean> = {};
    const hiddenKeys = new Set<string>();
    for (const f of entries) {
      if (f.source !== 'row' || f.notEnforced) continue;
      if (!formData.isAgency && PORTFOLIO_ONLY_GROUPS.has(f.group)) {
        hiddenKeys.add(f.key);
        continue;
      }
      if (f.key in draftToggles) entitlements[f.key] = draftToggles[f.key];
    }
    const quotas = Object.entries(draftQuotas)
      .filter(([, q]) => q.monthlyLimit !== null && !(q.featureKey && hiddenKeys.has(q.featureKey)))
      .map(([quotaKey, q]) => ({ quotaKey, monthlyLimit: q.monthlyLimit as number }));
    onSave({ ...formData, entitlements, quotas });
  };

  const updateField = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  // Integer limit fields. Raw text that is not a whole number ("1.5", "1e", "-") is kept visible in the input
  // (never normalized to 1) and blocks the submit; valid integers parse as before, empty = null/unlimited.
  const [invalidRaw, setInvalidRaw] = useState<Record<string, string>>({});
  const INTEGER_ERROR = 'Enter a whole number';

  const applyIntegerField = (field: string, input: HTMLInputElement) => {
    const raw = input.value;
    const parsed = parseIntegerInput(raw);
    if (input.validity.badInput || input.validity.stepMismatch || parsed.kind === 'invalid') {
      setInvalidRaw((prev) => ({ ...prev, [field]: raw }));
      return;
    }
    setInvalidRaw((prev) => {
      if (!(field in prev)) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
    const value = parsed.kind === 'ok' ? parsed.value : null;
    setFormData({ ...formData, [field]: value });
  };

  // Nullable limits: empty = null (unlimited), 0 = a real value.
  const updateNumberField = (field: string, input: HTMLInputElement) => applyIntegerField(field, input);

  // Required integer fields: empty stays empty (the required check blocks the submit), 0 stays 0.
  const updateRequiredIntegerField = (field: string, input: HTMLInputElement) =>
    applyIntegerField(field, input);

  // Required decimal fields (prices): decimals allowed.
  const updateRequiredNumberField = (field: string, input: HTMLInputElement, parse: (v: string) => number) => {
    if (input.validity.badInput) return;
    setFormData({ ...formData, [field]: input.value === '' ? null : parse(input.value) });
  };

  // Value shown in an integer input: the rejected raw text while invalid, else the saved value.
  const intValue = (field: keyof typeof formData): string | number =>
    field in invalidRaw ? invalidRaw[field] : ((formData[field] as number | null) ?? '');
  const intError = (field: string): string | undefined => (field in invalidRaw ? INTEGER_ERROR : undefined);

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={pkg ? 'Edit Package' : 'Create Package'}
      size="xl"
      footer={
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" form="package-form" loading={isSaving}>
            {pkg ? 'Update Package' : 'Create Package'}
          </Button>
        </ModalFooter>
      }
    >
      <form id="package-form" onSubmit={handleSubmit} className="space-y-6">
        {justCreated && pkg && (
          <div
            role="status"
            className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
            data-testid="package-created-notice"
          >
            Package created. Set feature access and monthly limits below; they save immediately.
          </div>
        )}

        {/* Basic Info */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Basic Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Package Name *
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                required
              />
            </div>
            <div>
              <Select
                label="Source"
                value={formData.source}
                onValueChange={(value) => updateField('source', value)}
                options={[
                  { value: 'DIRECT', label: 'DIRECT' },
                  { value: 'APPSUMO', label: 'APPSUMO' },
                  { value: 'STRIPE', label: 'STRIPE' },
                  { value: 'TRIAL', label: 'TRIAL' },
                  { value: 'MANUAL', label: 'MANUAL' }
                ]}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={3}
            />
          </div>
        </div>

        {/* Pricing */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Pricing</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price (Monthly) *
              </label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={formData.priceMonthly ?? ''}
                onChange={(e) => updateRequiredNumberField('priceMonthly', e.target, parseFloat)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price (Yearly)
              </label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={formData.priceYearly ?? ''}
                onChange={(e) => {
                  if (e.target.validity.badInput) return;
                  updateField('priceYearly', e.target.value ? parseFloat(e.target.value) : null);
                }}
              />
            </div>
          </div>
          {Number(formData.priceYearly) > 0 && (
            <p className="text-xs text-gray-500 italic">
              {pkg?.wcProductId && !pkg.wcVariationYearlyId
                ? "Note: this package's shop product is monthly-only. The yearly price is saved here but not synced (existing shop products are not converted)."
                : 'Note: with a yearly price, the shop product is created with a Monthly and a Yearly variation. Set both before the first shop sync.'}
            </p>
          )}
        </div>

        {/* Limits */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Limits (leave empty for unlimited)</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Channels
              </label>
              <Input
                type="number"
                min={0}
                step={1}
                value={intValue('maxChannels')}
                error={intError('maxChannels')}
                onChange={(e) => updateNumberField('maxChannels', e.target)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Users
              </label>
              <Input
                type="number"
                min={0}
                step={1}
                value={intValue('maxUsers')}
                error={intError('maxUsers')}
                onChange={(e) => updateNumberField('maxUsers', e.target)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Calendars
              </label>
              <Input
                type="number"
                min={0}
                step={1}
                value={intValue('maxCalendars')}
                error={intError('maxCalendars')}
                onChange={(e) => updateNumberField('maxCalendars', e.target)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Variations
              </label>
              <Input
                type="number"
                min={0}
                step={1}
                value={intValue('maxVariations')}
                error={intError('maxVariations')}
                onChange={(e) => updateNumberField('maxVariations', e.target)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Posts/Month
              </label>
              <Input
                type="number"
                min={0}
                step={1}
                value={intValue('maxPostsPerMonth')}
                error={intError('maxPostsPerMonth')}
                onChange={(e) => updateNumberField('maxPostsPerMonth', e.target)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max AI Tokens *
              </label>
              <Input
                type="number"
                min={0}
                step={1}
                value={intValue('maxAITokens')}
                error={intError('maxAITokens')}
                onChange={(e) => updateRequiredIntegerField('maxAITokens', e.target)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Storage (MB) *
              </label>
              <Input
                type="number"
                min={0}
                step={1}
                value={intValue('maxStorage')}
                error={intError('maxStorage')}
                onChange={(e) => updateRequiredIntegerField('maxStorage', e.target)}
                required
              />
            </div>
            {formData.isAgency && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand Spaces included
              </label>
              <p className="text-xs text-gray-500 mb-1">
                Brands per Brand Portfolio. Empty = unlimited. Extra Brand Spaces can be bought on top.
              </p>
              <Input
                type="number"
                min={0}
                step={1}
                value={intValue('maxWorkspaces')}
                error={intError('maxWorkspaces')}
                onChange={(e) => updateNumberField('maxWorkspaces', e.target)}
              />
            </div>
            )}
          </div>
        </div>

        {/* Features: existing packages use the instant-save Feature access section */}
        {pkg ? (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Package type</h3>
            <div className="grid grid-cols-2 gap-4">
              <Toggle
                label="Brand Portfolio Package"
                checked={formData.isAgency}
                onChange={(checked) => updateField('isAgency', checked)}
              />
              <div>
                <Toggle
                  label="Lifetime (one-time purchase)"
                  checked={formData.isLifetime}
                  onChange={(checked) => updateField('isLifetime', checked)}
                />
                <p className="text-xs text-gray-500 mt-1 ml-[52px]">
                  Creates a one-time WooCommerce product instead of a monthly subscription.
                </p>
              </div>
            </div>
          </div>
        ) : (
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Features</h3>
          <div className="grid grid-cols-2 gap-4">
            <Toggle
              label="Canva Enabled"
              checked={formData.canvaEnabled}
              onChange={(checked) => updateField('canvaEnabled', checked)}
            />
            <Toggle
              label="Brand Check Enabled"
              checked={formData.brandCheckEnabled}
              onChange={(checked) => updateField('brandCheckEnabled', checked)}
            />
            <Toggle
              label="Atelier Mode Enabled"
              checked={formData.whitelabelEnabled}
              onChange={(checked) => updateField('whitelabelEnabled', checked)}
            />
            <Toggle
              label="Brand Portfolio Package"
              checked={formData.isAgency}
              onChange={(checked) => updateField('isAgency', checked)}
            />
            <div>
              <Toggle
                label="Lifetime (one-time purchase)"
                checked={formData.isLifetime}
                onChange={(checked) => updateField('isLifetime', checked)}
              />
              <p className="text-xs text-gray-500 mt-1 ml-[52px]">
                Creates a one-time WooCommerce product instead of a monthly subscription.
              </p>
            </div>
            <Toggle
              label="Analytics Enabled"
              checked={formData.analyticsEnabled}
              onChange={(checked) => updateField('analyticsEnabled', checked)}
            />
            <Toggle
              label="Google Drive Enabled"
              checked={formData.gdriveEnabled}
              onChange={(checked) => updateField('gdriveEnabled', checked)}
            />
          </div>
        </div>
        )}

        {pkg ? (
          <PackageEntitlementsSection
            packageId={pkg.id}
            isAgency={formData.isAgency}
            maxBrandProfiles={formData.maxBrandProfiles}
            onMaxBrandProfilesInvalidChange={setBrandProfilesInvalid}
            onCommitMaxBrandProfiles={async (value) => {
              await onSaveBrandProfiles(pkg.id, value);
              // Keep the form (and the unsaved-changes baseline) in sync so a later Update sends the saved value
              initialFormDataRef.current = { ...initialFormDataRef.current, maxBrandProfiles: value };
              setFormData((prev) => ({ ...prev, maxBrandProfiles: value }));
              toast.success(`Brand World profiles per brand: ${value === null ? 'system maximum (10)' : value}`);
            }}
          />
        ) : (
          <PackageEntitlementsSection
            packageId={null}
            isAgency={formData.isAgency}
            maxBrandProfiles={formData.maxBrandProfiles}
            onMaxBrandProfilesInvalidChange={setBrandProfilesInvalid}
            onCommitMaxBrandProfiles={(value) => setFormData((prev) => ({ ...prev, maxBrandProfiles: value }))}
            draft={draft}
          />
        )}

        {/* Platforms: restrict which social platforms teams on this package may newly connect */}
        <div className="space-y-3" data-testid="package-platforms">
          <h3 className="font-semibold text-gray-900">Platforms</h3>
          <div>
            <Toggle
              label="Restrict platforms"
              checked={formData.platformsRestricted}
              onChange={(checked) => updateField('platformsRestricted', checked)}
            />
            <p className="text-xs text-gray-500 mt-1 ml-[52px]">
              Off: teams may connect every platform. On: only the ticked platforms can be newly connected.
              Existing channels on other platforms keep working.
            </p>
          </div>
          {formData.platformsRestricted && (
            <fieldset className="space-y-2">
              <legend className="sr-only">Allowed platforms</legend>
              <div className="grid grid-cols-1 min-[420px]:grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                {GATED_PLATFORMS.map((platform) => (
                  <Checkbox
                    key={platform}
                    id={`package-platform-${platform}`}
                    label={GATED_PLATFORM_LABELS[platform]}
                    checked={formData.allowedPlatforms.includes(platform)}
                    onCheckedChange={(checked) => togglePlatform(platform, checked)}
                  />
                ))}
              </div>
              {formData.allowedPlatforms.length === 0 && (
                <p role="status" className="text-xs text-amber-700">
                  No platform ticked: teams on this package cannot connect any new social channel.
                </p>
              )}
            </fieldset>
          )}
        </div>

        {/* Settings */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Settings</h3>
          <Toggle
            label="Active"
            checked={formData.isActive}
            onChange={(checked) => updateField('isActive', checked)}
          />
        </div>
      </form>
    </Modal>
  );
};

export default AdminPackages;
